// src/account-setup.schema.ts
import { z } from "zod";
var IndustryEnum = z.enum([
  "SOLICITORS",
  "ESTATE_AGENTS",
  "FINANCIAL_INSTITUTIONS",
  "ACCOUNTANCY",
  "TCSPS",
  "HIGH_VALUE_DEALERS",
  "GAMBLING_OPERATOR",
  "ART_MARKET",
  "CRYPTOASSET_EXCHANGE",
  "CUSTODIAN_WALLET"
]);
var AccountSetupSchema = z.object({
  // User Information
  title: z.string().min(1, "Title is required").max(20),
  forename: z.string().min(1, "Forename is required").max(100),
  middleName: z.string().max(100).optional(),
  surname: z.string().min(1, "Surname is required").max(100),
  // Company Information
  industry: IndustryEnum,
  companyName: z.string().min(1, "Company name is required").max(200),
  companyWebsite: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  // Address
  addressLine1: z.string().min(1, "Address line 1 is required").max(200),
  addressLine2: z.string().max(200).optional(),
  townCity: z.string().min(1, "Town/City is required").max(100),
  county: z.string().max(100).optional(),
  postcode: z.string().min(1, "Postcode is required").max(20),
  country: z.string().min(1, "Country is required").max(2),
  // Terms
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions"
  })
});

// src/auth.schema.ts
import { z as z2 } from "zod";
var LoginSchema = z2.object({
  email: z2.string().email("Invalid email address").describe("User email address"),
  password: z2.string().min(1, "Password is required").describe("User password")
});
var RegisterSchema = z2.object({
  email: z2.string().email("Invalid email address").describe("User email address"),
  password: z2.string().min(8, "Password must be at least 8 characters long").describe("User password (minimum 8 characters)")
});

// src/check-depth.schema.ts
import { z as z3 } from "zod";
var CheckDepthTierSchema = z3.enum(["basic", "standard", "enhanced"]);
var TIER_PRICES_PENCE = {
  basic: 250,
  // £2.50
  standard: 1e3,
  // £10.00
  enhanced: 2500
  // £25.00
};

// src/client-details.schema.ts
import { z as z4 } from "zod";
var ClientTypeSchema = z4.enum(["NEW", "EXISTING"]);
var EntityTypeSchema = z4.enum(["INDIVIDUAL", "ORGANISATION"]);
var ClientTitleSchema = z4.enum([
  // Civil
  "MR",
  "MRS",
  "MISS",
  "MS",
  "MX",
  // Professional/Academic
  "DR",
  "PROF",
  // Religious
  "REV",
  "FR",
  "SR",
  "BR",
  "BISHOP",
  "ARCHBISHOP",
  "CARDINAL",
  "CANON",
  "IMAM",
  "SHEIKH",
  "RABBI",
  "PASTOR",
  // Honour/Peerage/Political
  "SIR",
  "DAME",
  "LORD",
  "LADY",
  "BARON",
  "BARONESS",
  "THE_HON",
  "THE_RT_HON",
  // Judicial
  "JUDGE",
  "JUSTICE",
  "MAGISTRATE",
  // Military
  "CAPT",
  "CMDR",
  "COL",
  "MAJ",
  "LT",
  "SGT",
  "CPL",
  "WO",
  "ADM",
  "AIR_CDRE",
  "GP_CAPT",
  "FLT_LT",
  // Royal
  "HM",
  "HRH",
  // Formal
  "ESQ"
]);
var ClientTitleLabels = {
  MR: "Mr",
  MRS: "Mrs",
  MISS: "Miss",
  MS: "Ms",
  MX: "Mx",
  DR: "Dr",
  PROF: "Prof",
  REV: "Rev",
  FR: "Fr",
  SR: "Sr",
  BR: "Br",
  BISHOP: "Bishop",
  ARCHBISHOP: "Archbishop",
  CARDINAL: "Cardinal",
  CANON: "Canon",
  IMAM: "Imam",
  SHEIKH: "Sheikh",
  RABBI: "Rabbi",
  PASTOR: "Pastor",
  SIR: "Sir",
  DAME: "Dame",
  LORD: "Lord",
  LADY: "Lady",
  BARON: "Baron",
  BARONESS: "Baroness",
  THE_HON: "The Hon",
  THE_RT_HON: "The Rt Hon",
  JUDGE: "Judge",
  JUSTICE: "Justice",
  MAGISTRATE: "Magistrate",
  CAPT: "Capt",
  CMDR: "Cmdr",
  COL: "Col",
  MAJ: "Maj",
  LT: "Lt",
  SGT: "Sgt",
  CPL: "Cpl",
  WO: "WO",
  ADM: "Adm",
  AIR_CDRE: "Air Cdre",
  GP_CAPT: "Gp Capt",
  FLT_LT: "Flt Lt",
  HM: "HM",
  HRH: "HRH",
  ESQ: "Esq"
};
var MatterTypeSchema = z4.enum([
  "CONVEYANCING_RESIDENTIAL",
  "CONVEYANCING_COMMERCIAL",
  "PROBATE_AND_WILLS",
  "FAMILY_LAW",
  "LITIGATION",
  "CORPORATE_AND_COMMERCIAL",
  "IMMIGRATION",
  "EMPLOYMENT",
  "PERSONAL_INJURY",
  "CRIMINAL_DEFENCE",
  "OTHER"
]);
var MatterTypeLabels = {
  CONVEYANCING_RESIDENTIAL: "Conveyancing (Residential)",
  CONVEYANCING_COMMERCIAL: "Conveyancing (Commercial)",
  PROBATE_AND_WILLS: "Probate and Wills",
  FAMILY_LAW: "Family Law",
  LITIGATION: "Litigation",
  CORPORATE_AND_COMMERCIAL: "Corporate and Commercial",
  IMMIGRATION: "Immigration",
  EMPLOYMENT: "Employment",
  PERSONAL_INJURY: "Personal Injury",
  CRIMINAL_DEFENCE: "Criminal Defence",
  OTHER: "Other"
};
var ClientCompanySchema = z4.object({
  id: z4.string().uuid().optional(),
  // Optional for new companies
  companyName: z4.string().min(1, "Company name is required"),
  dateOfIncorporation: z4.coerce.date().nullable().optional(),
  addressLine1: z4.string().optional(),
  addressLine2: z4.string().optional(),
  townCity: z4.string().optional(),
  county: z4.string().optional(),
  postcode: z4.string().optional(),
  country: z4.string().default("GB")
});
var DraftClientCompanySchema = z4.object({
  id: z4.string().uuid().optional(),
  companyName: z4.string().optional(),
  // No min length for drafts
  dateOfIncorporation: z4.coerce.date().nullable().optional(),
  addressLine1: z4.string().optional(),
  addressLine2: z4.string().optional(),
  townCity: z4.string().optional(),
  county: z4.string().optional(),
  postcode: z4.string().optional(),
  country: z4.string().optional()
});
var CreateClientDetailsSchema = z4.object({
  // Client Reference & Type
  clientRef: z4.string().min(1, "Client reference is required"),
  clientType: ClientTypeSchema.default("NEW"),
  entityType: EntityTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  // Handle empty string from form select
  matterType: MatterTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  // Handle empty string from form select
  // Legal matter flag (stored on RiskAssessment, controls SRA form visibility)
  isLegalMatter: z4.boolean().default(false),
  // Personal Information
  titles: z4.array(ClientTitleSchema).max(10, "Maximum of 10 titles allowed").optional().nullable(),
  forename: z4.string().min(1, "Forename is required"),
  middleName: z4.string().optional().nullable(),
  surname: z4.string().min(1, "Surname is required"),
  dateOfBirth: z4.coerce.date().refine((date) => date < /* @__PURE__ */ new Date(), {
    message: "Date of birth must be in the past"
  }),
  nationality: z4.string().min(2, "Nationality is required"),
  countryOfResidence: z4.string().min(2, "Country of residence is required"),
  // Address
  addressLine1: z4.string().optional().nullable(),
  addressLine2: z4.string().optional().nullable(),
  townCity: z4.string().min(1, "Town/City is required").optional().nullable(),
  county: z4.string().optional().nullable(),
  postcode: z4.string().optional().nullable(),
  // Occupation
  occupation: z4.string().min(1, "Occupation/Source of income is required"),
  // Companies (optional)
  companies: z4.array(ClientCompanySchema).optional(),
  // Consent (required for submission)
  consentGiven: z4.boolean().refine((val) => val === true, {
    message: "You must confirm consent to proceed"
  })
});
var SaveDraftClientDetailsSchema = z4.object({
  clientRef: z4.string().optional(),
  clientType: ClientTypeSchema.optional(),
  entityType: EntityTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  matterType: MatterTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  isLegalMatter: z4.boolean().optional(),
  titles: z4.array(ClientTitleSchema).max(10).optional().nullable(),
  forename: z4.string().optional(),
  middleName: z4.string().optional().nullable(),
  surname: z4.string().optional(),
  dateOfBirth: z4.coerce.date().optional().nullable(),
  nationality: z4.string().optional(),
  countryOfResidence: z4.string().optional(),
  addressLine1: z4.string().optional().nullable(),
  addressLine2: z4.string().optional().nullable(),
  townCity: z4.string().optional().nullable(),
  county: z4.string().optional().nullable(),
  postcode: z4.string().optional().nullable(),
  occupation: z4.string().optional(),
  companies: z4.array(DraftClientCompanySchema).optional(),
  consentGiven: z4.boolean().optional()
});
var ClientDetailsResponseSchema = z4.object({
  id: z4.string().uuid(),
  clientRef: z4.string(),
  clientType: ClientTypeSchema,
  entityType: EntityTypeSchema.nullable(),
  matterType: MatterTypeSchema.nullable(),
  titles: z4.array(ClientTitleSchema).nullable(),
  forename: z4.string(),
  middleName: z4.string().nullable(),
  surname: z4.string(),
  dateOfBirth: z4.coerce.date(),
  nationality: z4.string(),
  countryOfResidence: z4.string(),
  addressLine1: z4.string().nullable(),
  addressLine2: z4.string().nullable(),
  townCity: z4.string(),
  county: z4.string().nullable(),
  postcode: z4.string().nullable(),
  occupation: z4.string(),
  consentGiven: z4.boolean(),
  consentTimestamp: z4.coerce.date().nullable(),
  riskAssessmentId: z4.string().uuid(),
  companies: z4.array(
    ClientCompanySchema.extend({
      id: z4.string().uuid(),
      clientDetailsId: z4.string().uuid(),
      createdAt: z4.coerce.date(),
      updatedAt: z4.coerce.date()
    })
  ).optional(),
  createdAt: z4.coerce.date(),
  updatedAt: z4.coerce.date()
});
var StartAssessmentResponseSchema = z4.object({
  id: z4.string().uuid(),
  optimalKycRef: z4.string(),
  status: z4.literal("IN_PROGRESS"),
  createdAt: z4.coerce.date()
});

// src/env.schema.ts
import { z as z5 } from "zod";
var EnvironmentSchema = z5.enum(["local", "development", "staging", "production", "test"]);
var WorkerModeSchema = z5.enum(["api", "worker", "both"]);
var WorkerTypeSchema = z5.enum(["browser", "compute", "all"]);
var EnvSchema = z5.object({
  NODE_ENV: EnvironmentSchema.default("local").describe("Application environment"),
  PORT: z5.string().regex(/^\d+$/, "PORT must be a number").transform((val) => parseInt(val, 10)).pipe(z5.number().int().min(1).max(65535)).default("3000").describe("Server port (1-65535)"),
  DATABASE_URL: z5.string().min(1, "DATABASE_URL is required").describe("PostgreSQL connection string"),
  JWT_ACCESS_SECRET: z5.string().min(1, "JWT_ACCESS_SECRET is required").describe("Secret for access tokens"),
  JWT_REFRESH_SECRET: z5.string().min(1, "JWT_REFRESH_SECRET is required").describe("Secret for refresh tokens"),
  COOKIE_SECRET: z5.string().min(1, "COOKIE_SECRET is required").describe("Secret for cookie signing"),
  RATE_LIMIT_MAX: z5.string().regex(/^\d+$/, "RATE_LIMIT_MAX must be a number").transform((val) => parseInt(val, 10)).pipe(z5.number().int().min(1)).default("100").describe("Maximum requests per rate limit window"),
  OTEL_EXPORTER_OTLP_ENDPOINT: z5.string().optional().describe("OpenTelemetry collector endpoint (optional)"),
  PROM_AUTH_USER: z5.string().optional().describe("Prometheus basic auth username (optional)"),
  PROM_AUTH_PASS: z5.string().optional().describe("Prometheus basic auth password (optional)"),
  // Mailtrap SMTP Configuration
  SMTP_HOST: z5.string().default("sandbox.smtp.mailtrap.io").describe("SMTP server host"),
  SMTP_PORT: z5.string().regex(/^\d+$/, "SMTP_PORT must be a number").transform((val) => parseInt(val, 10)).pipe(z5.number().int().min(1).max(65535)).default("2525").describe("SMTP server port"),
  SMTP_USER: z5.string().optional().describe("SMTP username (Mailtrap credentials)"),
  SMTP_PASS: z5.string().optional().describe("SMTP password (Mailtrap credentials)"),
  SMTP_FROM_EMAIL: z5.string().email().default("noreply@okyc.ai").describe("From email address"),
  SMTP_FROM_NAME: z5.string().default("Optimal KYC").describe("From name"),
  // Frontend URL for password reset links
  FRONTEND_URL: z5.string().url().default("http://localhost:5173").describe("Frontend application URL"),
  // Evidence Storage Configuration
  STORAGE_TYPE: z5.enum(["local", "s3"]).default("local").describe("Storage provider type: local (filesystem) or s3"),
  EVIDENCE_STORAGE_PATH: z5.string().optional().describe("Local storage path for evidence files (only used when STORAGE_TYPE=local)"),
  S3_EVIDENCE_BUCKET: z5.string().optional().describe("S3 bucket name for evidence storage (required when STORAGE_TYPE=s3)"),
  S3_EVIDENCE_PREFIX: z5.string().default("evidence").describe("S3 key prefix for evidence files"),
  AWS_REGION: z5.string().default("eu-west-2").describe("AWS region for S3 bucket"),
  AWS_BEDROCK_MODEL_ID: z5.string().default("openai.gpt-oss-120b-1:0").refine(
    (val) => !val.toLowerCase().includes("anthropic") && !val.toLowerCase().includes("claude"),
    { message: "Anthropic/Claude models are not allowed. Use openai.gpt-oss-120b-1:0" }
  ).describe("AWS Bedrock model ID (must be gpt-oss-120b)"),
  // Agent API Keys
  COMPANIES_HOUSE_API_KEY: z5.string().optional().describe("Companies House API key for officer/company lookups"),
  SEARCH_PROVIDER: z5.enum(["brave", "google"]).optional().default("brave").describe("Search provider for adverse media and PEP screening (brave or google)"),
  BRAVE_SEARCH_API_KEY: z5.string().optional().describe("Brave Search API key for adverse media and PEP screening"),
  GOOGLE_API_KEY: z5.string().optional().describe("Google Custom Search API key"),
  GOOGLE_SEARCH_ENGINE_ID: z5.string().optional().describe("Google Custom Search Engine ID (cx parameter)"),
  BRAVE_GOGGLES_ENABLED: z5.string().optional().default("true").describe("Enable Brave Search Goggles to boost trusted media sources (true/false)"),
  BRAVE_GOGGLES_URL: z5.string().optional().describe(
    "URL to hosted Goggles file (e.g. https://your-domain.com/api/goggles/media-sources). If not set, rules are sent inline."
  ),
  // Queue Configuration
  REDIS_URL: z5.string().optional().describe("Redis connection URL for job queue (e.g., redis://localhost:6379)"),
  WORKER_MODE: WorkerModeSchema.default("api").describe(
    "Instance mode: api (HTTP only), worker (queue only), both (legacy combined mode)"
  ),
  WORKER_TYPE: WorkerTypeSchema.default("all").describe(
    "Queue type to process: browser (Chromium jobs), compute (AI jobs), all (development)"
  ),
  DISABLE_NODE_WORKER: z5.enum(["true", "false"]).default("false").transform((val) => val === "true").describe("Disable in-process queue consumer (rely on ECS workers)"),
  // Parity Runner Configuration
  PARITY_RUNNER_ENABLED: z5.enum(["true", "false"]).default("false").transform((val) => val === "true").describe("Enable parity runner evaluation system (admin only)"),
  // GDPR/Support Contact Configuration
  GDPR_CONTACT_EMAIL: z5.string().email().default("dpo@okyc.ai").describe("Data Protection Officer contact email"),
  SUPPORT_EMAIL: z5.string().email().default("support@okyc.ai").describe("Support team contact email"),
  // Stripe Payment Configuration
  STRIPE_SECRET_KEY: z5.string().optional().describe("Stripe secret API key (sk_test_... or sk_live_...)"),
  STRIPE_WEBHOOK_SECRET: z5.string().optional().describe("Stripe webhook signing secret (whsec_...)"),
  STRIPE_PUBLISHABLE_KEY: z5.string().optional().describe("Stripe publishable key for frontend (pk_test_... or pk_live_...)"),
  // CORS Configuration
  CORS_ALLOWED_ORIGINS: z5.string().optional().describe(
    "Comma-separated list of allowed CORS origins (e.g., https://app.okyc.ai,https://staging.okyc.ai). If not set, CORS is disabled."
  ),
  // Document Upload Configuration
  UPLOAD_TOKEN_EXPIRY_DAYS: z5.string().regex(/^\d+$/, "UPLOAD_TOKEN_EXPIRY_DAYS must be a number").transform((val) => parseInt(val, 10)).pipe(z5.number().int().min(1).max(30)).optional().describe("Upload token expiry in days (default: 7, max: 30)"),
  UPLOAD_DOCUMENT_EXPIRY_DAYS: z5.string().regex(/^\d+$/, "UPLOAD_DOCUMENT_EXPIRY_DAYS must be a number").transform((val) => parseInt(val, 10)).pipe(z5.number().int().min(1).max(90)).optional().describe("Document retention period in days after upload (default: 10, max: 90)"),
  // Risk Scoring Configuration
  MAX_RISK_SCORE: z5.string().regex(/^\d+$/, "MAX_RISK_SCORE must be a number").transform((val) => parseInt(val, 10)).pipe(z5.number().int().min(0).max(100)).optional().describe("Maximum risk score cap (0-100). Defaults to 99.")
}).refine((data) => data.STORAGE_TYPE !== "s3" || !!data.S3_EVIDENCE_BUCKET, {
  message: "S3_EVIDENCE_BUCKET is required when STORAGE_TYPE is s3"
}).refine(
  (data) => {
    if (data.WORKER_MODE !== "api" && !data.REDIS_URL) {
      return false;
    }
    return true;
  },
  {
    message: "REDIS_URL is required when WORKER_MODE is worker or both"
  }
).refine(
  (data) => {
    if (data.STRIPE_SECRET_KEY && !data.STRIPE_WEBHOOK_SECRET) {
      return false;
    }
    return true;
  },
  {
    message: "STRIPE_WEBHOOK_SECRET is required when STRIPE_SECRET_KEY is set"
  }
);

// src/feature-flag.schema.ts
import { z as z6 } from "zod";
var JsonValueSchema = z6.lazy(
  () => z6.union([
    z6.string(),
    z6.number(),
    z6.boolean(),
    z6.record(JsonValueSchema),
    z6.array(JsonValueSchema)
  ])
);
var CreateFeatureFlagSchema = z6.object({
  key: z6.string().min(1, "Key is required").regex(/^[a-z0-9_]+$/, "Key must contain only lowercase letters, numbers, and underscores").describe("Unique feature flag key (snake_case)"),
  enabled: z6.boolean().default(false).describe("Whether the feature flag is enabled"),
  description: z6.string().optional().describe("Human-readable description of the feature flag"),
  strategies: JsonValueSchema.optional().describe("Targeting strategies (JSON object)")
});
var UpdateFeatureFlagSchema = z6.object({
  enabled: z6.boolean().optional().describe("Whether the feature flag is enabled"),
  description: z6.string().optional().describe("Human-readable description of the feature flag"),
  strategies: JsonValueSchema.optional().describe("Targeting strategies (JSON object)")
});
var FeatureFlagSchema = z6.object({
  key: z6.string(),
  enabled: z6.boolean(),
  description: z6.string().nullable(),
  strategies: z6.union([JsonValueSchema, z6.null()]),
  createdAt: z6.date(),
  updatedAt: z6.date()
});

// src/password-reset.schema.ts
import { z as z7 } from "zod";
var ForgotPasswordSchema = z7.object({
  email: z7.string().email("Invalid email address").describe("User email address")
});
var ResetPasswordSchema = z7.object({
  token: z7.string().min(1, "Reset token is required").describe("Password reset token"),
  password: z7.string().min(8, "Password must be at least 8 characters").describe("New password"),
  confirmPassword: z7.string().min(1, "Please confirm your password").describe("Password confirmation")
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

// src/payment.schema.ts
import { z as z8 } from "zod";
var MIN_PAYMENT_AMOUNT_PENCE = 500;
var MAX_PAYMENT_AMOUNT_PENCE = 1e6;
var CreatePaymentIntentSchema = z8.object({
  amount: z8.number().int("Amount must be a whole number (pence)").min(MIN_PAYMENT_AMOUNT_PENCE, `Minimum amount is ${MIN_PAYMENT_AMOUNT_PENCE} pence (\xA35.00)`).max(
    MAX_PAYMENT_AMOUNT_PENCE,
    `Maximum amount is ${MAX_PAYMENT_AMOUNT_PENCE} pence (\xA310,000.00)`
  ).describe("Amount in pence (e.g., 500 = \xA35.00)")
});
var PaymentIntentResponseSchema = z8.object({
  clientSecret: z8.string().describe("Stripe client secret for confirming payment"),
  paymentIntentId: z8.string().describe("Stripe PaymentIntent ID"),
  amount: z8.number().describe("Amount in pence")
});
var PaymentStatusSchema = z8.enum([
  "PENDING",
  "PROCESSING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED"
]);

// src/project.schema.ts
import { z as z9 } from "zod";
var ProjectSchema = z9.object({
  id: z9.string().uuid().describe("Project unique identifier"),
  name: z9.string().min(3, "Name must be at least 3 characters").describe("Project name"),
  description: z9.string().nullable().optional().describe("Project description (optional)"),
  userId: z9.string().uuid().describe("Owner user ID"),
  createdAt: z9.date().describe("Creation timestamp"),
  updatedAt: z9.date().describe("Last update timestamp")
});
var CreateProjectSchema = z9.object({
  name: z9.string().min(1, "Project name is required").max(100, "Project name must not exceed 100 characters").describe("The name of the project"),
  description: z9.string().max(500, "Description must not exceed 500 characters").optional().describe("A brief description of the project")
});
var UpdateProjectSchema = z9.object({
  name: z9.string().min(1, "Project name is required").max(100, "Project name must not exceed 100 characters").optional().describe("The name of the project"),
  description: z9.string().max(500, "Description must not exceed 500 characters").optional().describe("A brief description of the project")
});

// src/risk-assessment.schema.ts
import { z as z10 } from "zod";
var RiskLevelSchema = z10.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]);
var DocumentRequestSchema = z10.object({
  id: z10.string(),
  documentType: z10.string(),
  otherDescription: z10.string().optional(),
  dateRequested: z10.coerce.date(),
  received: z10.boolean(),
  downloadUrl: z10.string().optional()
});
var AssessmentStatusSchema = z10.enum(["IN_PROGRESS", "COMPLETED", "DELETED"]);
var ScreeningJobStatusSchema = z10.enum([
  "NOT_STARTED",
  // No screening has been initiated
  "FAST_CHECKS_DONE",
  // Quick checks complete, adverse media queued
  "PROCESSING",
  // Adverse media check in progress
  "COMPLETED",
  // All checks complete
  "FAILED"
  // Processing failed after retries
]);
var ScreeningCheckIdSchema = z10.enum([
  "UK",
  // HM Treasury Sanctions List
  "US",
  // US Consolidated Sanctions List (OFAC)
  "UN",
  // UN Sanctions List
  "EU",
  // EU Sanctions List
  "PEP",
  // Politically Exposed Persons
  "ADVERSE_MEDIA",
  // Adverse Media screening
  "COMPANIES_HOUSE",
  // Companies House officer search
  "CPI",
  // Corruption Perceptions Index
  "FATF"
  // Financial Action Task Force jurisdictions
]);
var ALL_SCREENING_CHECKS = [
  "UK",
  "US",
  "UN",
  "EU",
  "PEP",
  "ADVERSE_MEDIA",
  "COMPANIES_HOUSE",
  "CPI",
  "FATF"
];
var REQUIRED_CHECKS = ["UK"];
var PaymentFlowStepSchema = z10.enum(["REQUEST_DOCS", "MATCH_REPORT", "EDD"]);
var RiskAssessmentSchema = z10.object({
  id: z10.string(),
  optimalKycRef: z10.string(),
  // e.g., "OK10"
  clientRef: z10.string(),
  // e.g., "SRA013"
  clientName: z10.string(),
  clientEmail: z10.string().email().optional(),
  riskLevel: RiskLevelSchema,
  status: AssessmentStatusSchema,
  screeningJobStatus: ScreeningJobStatusSchema.optional(),
  // Queue processing status
  paymentFlowStep: PaymentFlowStepSchema.nullable().optional(),
  // Current step in payment flow for resumption
  isLegalMatter: z10.boolean().default(false),
  // Whether this is a legal matter (for SRA form generation)
  documentRequestLinkGenerated: z10.boolean(),
  documentRequests: z10.array(DocumentRequestSchema).optional(),
  hasNewDocuments: z10.boolean().default(false),
  reportDownloadWindowDays: z10.number().nullable(),
  // null = expired
  reportExpired: z10.boolean().default(false),
  needsRefresh: z10.boolean().default(false),
  // Whether assessment needs refresh based on user's due diligence frequency
  reportData: z10.record(z10.unknown()).nullable().optional(),
  // FinalResult JSON from screening
  formattedReport: z10.string().nullable().optional(),
  // Human-readable screening report
  enabledChecks: z10.array(ScreeningCheckIdSchema).optional(),
  // Which checks were run
  dateReportGenerated: z10.coerce.date().nullable(),
  dateDeleted: z10.coerce.date().nullable(),
  createdAt: z10.coerce.date(),
  updatedAt: z10.coerce.date()
});
var RiskAssessmentListResponseSchema = z10.object({
  data: z10.array(RiskAssessmentSchema),
  total: z10.number(),
  page: z10.number(),
  pageSize: z10.number()
});
var RiskAssessmentSortColumnSchema = z10.enum([
  "clientRef",
  "riskLevel",
  "dateReportGenerated",
  "createdAt"
]);
var RiskAssessmentFiltersSchema = z10.object({
  search: z10.string().optional(),
  riskLevels: z10.union([RiskLevelSchema, z10.array(RiskLevelSchema)]).optional().transform((val) => val ? Array.isArray(val) ? val : [val] : void 0),
  reportDownloadWindow: z10.coerce.number().optional(),
  dateFrom: z10.coerce.date().optional(),
  dateTo: z10.coerce.date().optional(),
  status: AssessmentStatusSchema.optional(),
  page: z10.coerce.number().default(1),
  pageSize: z10.coerce.number().default(20),
  sortBy: RiskAssessmentSortColumnSchema.optional(),
  sortOrder: z10.enum(["asc", "desc"]).optional()
});
var GenerateDocumentLinkSchema = z10.object({
  assessmentId: z10.string(),
  clientEmail: z10.string().email(),
  documentTypes: z10.array(z10.string()).min(1),
  otherDescription: z10.string().optional()
});
var DocumentTypeSchema = z10.enum([
  "IDENTITY_DOCUMENT",
  "PROOF_OF_ADDRESS",
  "PROOF_OF_INCOME",
  "BANK_STATEMENT",
  "TAX_RETURN",
  "OTHER"
]);
var DocumentTypeLabels = {
  IDENTITY_DOCUMENT: "Identity Document",
  PROOF_OF_ADDRESS: "Proof of Address",
  PROOF_OF_INCOME: "Proof of Income",
  BANK_STATEMENT: "Bank Statement",
  TAX_RETURN: "Tax Return",
  OTHER: "Other"
};
var CreateRiskAssessmentSchema = z10.object({
  clientRef: z10.string().min(1),
  clientName: z10.string().min(1),
  clientEmail: z10.string().email().optional(),
  riskLevel: RiskLevelSchema,
  reportData: z10.record(z10.unknown()).optional(),
  // FinalResult JSON
  formattedReport: z10.string().optional()
});
var UpdateRiskAssessmentSchema = z10.object({
  clientRef: z10.string().min(1).optional(),
  clientName: z10.string().min(1).optional(),
  clientEmail: z10.string().email().optional(),
  riskLevel: RiskLevelSchema.optional(),
  status: AssessmentStatusSchema.optional()
});
var BulkOperationSchema = z10.object({
  ids: z10.array(z10.string().uuid()).min(1)
});

// src/settings.schema.ts
import { z as z11 } from "zod";
var DueDiligenceFrequencyEnum = z11.enum(["MONTHLY", "QUARTERLY", "ANNUALLY", "NEVER"]);
var UpdateProfileSchema = z11.object({
  title: z11.string().min(1, "Title is required").max(20).optional(),
  forename: z11.string().min(1, "Forename is required").max(100).optional(),
  middleName: z11.string().max(100).nullable().optional(),
  surname: z11.string().min(1, "Surname is required").max(100).optional()
});
var UpdateCompanySchema = z11.object({
  name: z11.string().min(1, "Company name is required").max(200).optional(),
  industry: IndustryEnum.optional(),
  website: z11.string().url("Please enter a valid URL").nullable().optional(),
  addressLine1: z11.string().min(1, "Address line 1 is required").max(200).optional(),
  addressLine2: z11.string().max(200).nullable().optional(),
  townCity: z11.string().min(1, "Town/City is required").max(100).optional(),
  county: z11.string().max(100).nullable().optional(),
  postcode: z11.string().min(1, "Postcode is required").max(20).optional(),
  country: z11.string().min(2, "Country is required").max(2).optional()
});
var UpdateDueDiligenceSchema = z11.object({
  frequency: DueDiligenceFrequencyEnum
});
var DeleteAccountSchema = z11.object({
  confirmEmail: z11.string().email("Please enter your email"),
  password: z11.string().min(1, "Password is required")
});
var SettingsFormSchema = z11.object({
  // User Info
  title: z11.string().min(1, "Title is required"),
  forename: z11.string().min(1, "Forename is required"),
  middleName: z11.string().optional(),
  surname: z11.string().min(1, "Surname is required"),
  // Due Diligence
  dueDiligenceAlertFrequency: DueDiligenceFrequencyEnum,
  // Company
  companyName: z11.string().min(1, "Company name is required"),
  industry: IndustryEnum,
  companyWebsite: z11.string().url("Please enter a valid URL").optional().or(z11.literal("")),
  // Address
  addressLine1: z11.string().min(1, "Address line 1 is required"),
  addressLine2: z11.string().optional(),
  townCity: z11.string().min(1, "Town/City is required"),
  county: z11.string().optional(),
  postcode: z11.string().min(1, "Postcode is required"),
  country: z11.string().min(2).max(2)
});
var PricingItemSchema = z11.object({
  key: z11.string(),
  name: z11.string(),
  description: z11.string().nullable(),
  priceGBP: z11.string()
  // Decimal as string for precision
});
var ProfileResponseSchema = z11.object({
  user: z11.object({
    id: z11.string(),
    email: z11.string(),
    title: z11.string().nullable(),
    forename: z11.string().nullable(),
    middleName: z11.string().nullable(),
    surname: z11.string().nullable(),
    dueDiligenceAlertFrequency: DueDiligenceFrequencyEnum,
    balance: z11.string()
    // Decimal as string
  }),
  company: z11.object({
    id: z11.string(),
    name: z11.string(),
    industry: IndustryEnum,
    website: z11.string().nullable(),
    addressLine1: z11.string().nullable(),
    addressLine2: z11.string().nullable(),
    townCity: z11.string().nullable(),
    county: z11.string().nullable(),
    postcode: z11.string().nullable(),
    country: z11.string()
  }).nullable()
});

// src/user.schema.ts
import { z as z12 } from "zod";
var RoleSchema = z12.object({
  id: z12.string().uuid(),
  name: z12.string().min(1),
  description: z12.string().nullable(),
  createdAt: z12.date(),
  updatedAt: z12.date()
});
var CompanySchema = z12.object({
  id: z12.string().uuid(),
  name: z12.string(),
  industry: IndustryEnum,
  website: z12.string().nullable(),
  addressLine1: z12.string().nullable(),
  addressLine2: z12.string().nullable(),
  townCity: z12.string().nullable(),
  county: z12.string().nullable(),
  postcode: z12.string().nullable(),
  country: z12.string(),
  createdAt: z12.date(),
  updatedAt: z12.date()
});
var PermissionSchema = z12.object({
  id: z12.string().uuid(),
  name: z12.string().min(1),
  description: z12.string().nullable(),
  createdAt: z12.date(),
  updatedAt: z12.date()
});
var UserSchema = z12.object({
  id: z12.string().uuid(),
  email: z12.string().email(),
  passwordHash: z12.string(),
  roleId: z12.string().uuid(),
  role: RoleSchema.optional(),
  failedLoginAttempts: z12.number().int().min(0),
  lockedUntil: z12.date().nullable(),
  // Personal Information (from account setup)
  title: z12.string().nullable(),
  forename: z12.string().nullable(),
  middleName: z12.string().nullable(),
  surname: z12.string().nullable(),
  accountSetupComplete: z12.boolean(),
  // Company relationship
  companyId: z12.string().uuid().nullable(),
  createdAt: z12.date(),
  updatedAt: z12.date()
});
var PublicUserSchema = UserSchema.omit({
  passwordHash: true,
  failedLoginAttempts: true,
  lockedUntil: true
}).extend({
  role: z12.object({
    id: z12.string().uuid(),
    name: z12.string(),
    description: z12.string().nullable()
  }).optional(),
  company: CompanySchema.nullable().optional(),
  balance: z12.union([z12.string(), z12.number()]).transform((val) => Number(val))
});
var CreateUserSchema = z12.object({
  email: z12.string().email("Invalid email address").describe("User email address"),
  password: z12.string().min(8, "Password must be at least 8 characters").describe("User password")
});

// src/utils.ts
import { z as z13 } from "zod";
function createUpdateSchema(schema) {
  const shape = schema.shape;
  const optionalShape = Object.keys(shape).reduce(
    (acc, key) => {
      acc[key] = shape[key].optional();
      return acc;
    },
    {}
  );
  return z13.object(optionalShape);
}
function omitSensitive(schema, keys) {
  const omitKeys = keys.reduce(
    (acc, key) => {
      acc[key] = true;
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {}
  );
  return schema.omit(omitKeys);
}
export {
  ALL_SCREENING_CHECKS,
  AccountSetupSchema,
  AssessmentStatusSchema,
  BulkOperationSchema,
  CheckDepthTierSchema,
  ClientCompanySchema,
  ClientDetailsResponseSchema,
  ClientTitleLabels,
  ClientTitleSchema,
  ClientTypeSchema,
  CompanySchema,
  CreateClientDetailsSchema,
  CreateFeatureFlagSchema,
  CreatePaymentIntentSchema,
  CreateProjectSchema,
  CreateRiskAssessmentSchema,
  CreateUserSchema,
  DeleteAccountSchema,
  DocumentRequestSchema,
  DocumentTypeLabels,
  DocumentTypeSchema,
  DraftClientCompanySchema,
  DueDiligenceFrequencyEnum,
  EntityTypeSchema,
  EnvSchema,
  EnvironmentSchema,
  FeatureFlagSchema,
  ForgotPasswordSchema,
  GenerateDocumentLinkSchema,
  IndustryEnum,
  LoginSchema,
  MAX_PAYMENT_AMOUNT_PENCE,
  MIN_PAYMENT_AMOUNT_PENCE,
  MatterTypeLabels,
  MatterTypeSchema,
  PaymentFlowStepSchema,
  PaymentIntentResponseSchema,
  PaymentStatusSchema,
  PermissionSchema,
  PricingItemSchema,
  ProfileResponseSchema,
  ProjectSchema,
  PublicUserSchema,
  REQUIRED_CHECKS,
  RegisterSchema,
  ResetPasswordSchema,
  RiskAssessmentFiltersSchema,
  RiskAssessmentListResponseSchema,
  RiskAssessmentSchema,
  RiskAssessmentSortColumnSchema,
  RiskLevelSchema,
  RoleSchema,
  SaveDraftClientDetailsSchema,
  ScreeningCheckIdSchema,
  ScreeningJobStatusSchema,
  SettingsFormSchema,
  StartAssessmentResponseSchema,
  TIER_PRICES_PENCE,
  UpdateCompanySchema,
  UpdateDueDiligenceSchema,
  UpdateFeatureFlagSchema,
  UpdateProfileSchema,
  UpdateProjectSchema,
  UpdateRiskAssessmentSchema,
  UserSchema,
  WorkerModeSchema,
  WorkerTypeSchema,
  createUpdateSchema,
  omitSensitive
};
//# sourceMappingURL=index.js.map